import { Component, Input, OnInit } from '@angular/core';
import { DateChangeEvent, SchedulerEvent } from '@progress/kendo-angular-scheduler';
import { AdminService } from '../../services/admin.service';
import moment from 'moment';
import { endOfWorkWeek, formatEndToAPI, formatStartToAPI, startOfWorkWeek } from '../../constants/scheduler';
import { SchedulerEditService } from 'src/app/shared/services/scheduler-edit.service';
import { SVGIcon, xIcon } from '@progress/kendo-svg-icons';
import {
  faCalendar,
  faChevronDown,
  faClock,
  faLocationDot,
  faStairs,
  faUsers,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import { filter, Observable, takeUntil } from 'rxjs';
import { DialogCloseResult, DialogResult } from '@progress/kendo-angular-dialog';
import { ScheduleTimeComponent } from 'src/app/shared/components/schedule-time/schedule-time.component';
import { ScheduleDialogService } from 'src/app/shared/services/schedule-dialog.service';
import { FormGroup } from '@angular/forms';
import { AbstractComponent } from 'src/app/shared/components/abstract-component';
import { ActivatedRoute } from '@angular/router';
import { IProviderManagementInfo } from 'src/app/modules/provider-management/constants/provider-management-routes';
import { Assignments, ScheduleEventSaveModel } from 'src/app/modules/provider-management/models/schedule-save-model';
import { GetMatchingSchedulesResponse } from 'src/app/modules/provider-management/interfaces/matching-schedule';
import { DashboardPMResponse } from 'src/app/modules/provider-management/interfaces/pm-dashboard.response';
import { AuthStore } from 'src/app/stores/auth.store';
import { User } from 'src/app/shared/models/user';
import { Occurrences, ScheduleTimeSubType, ScheduleTimeType } from 'src/app/modules/provider-management/enums/schedule-enums';
import { SchedulerStatus } from 'src/app/shared/enums/scheduler-status';
import { ICurrentEventData } from 'src/app/modules/provider-management/interfaces/current-event-data';
import { PopoverAnchorDirective } from '@progress/kendo-angular-tooltip';
import { ReferenceStore } from 'src/app/stores/reference.store';
import { IReferenceData } from 'src/app/shared/interfaces/form/reference-data.model';

const IEP_MEETING_ATTENDANCE_NUMBER: number = 4;
const NYCPS_AUTHORIZED_TRAINING: number = 3;
const MEETING_WITH_SCHOOL_STAFF: number = 2;
const FA_ICON_PATH_INDEX: number = 4;
const FA_ICON_WIDTH_INDEX: number = 0;
const FA_ICON_HEIGHT_INDEX: number = 1;

interface ScheduleEventFormValue {
  studentMandates: DashboardPMResponse[];
  occurences: { id: number };
  sessionType: { id: number };
  startDate: Date;
  endByDate: Date;
}

@Component({
  selector: 'app-agency-schedule',
  templateUrl: './agency-schedule.component.html',
  styleUrls: ['./agency-schedule.component.scss'],
  standalone: false,
})
export class AgencyScheduleComponent extends AbstractComponent implements OnInit {
  @Input() providerSk: string;
  public selectedDate: Date = moment().toDate();
  public events: SchedulerEvent[]; // Ensure it's initialized
  currentYear: number = new Date().getFullYear();
  readonly iEPMeetingAttendanceNumber: number = IEP_MEETING_ATTENDANCE_NUMBER;
  readonly nYCPSAuthorizedTraining: number = NYCPS_AUTHORIZED_TRAINING;
  readonly meetingWithSchoolStaff: number = MEETING_WITH_SCHOOL_STAFF;
  public clockIcon: SVGIcon = this.customFaIcon(faClock);
  public currentEventData: ICurrentEventData;
  public users: SVGIcon = this.customFaIcon(faUsers);
  public locationDot: SVGIcon = this.customFaIcon(faLocationDot);
  public stairs: SVGIcon = this.customFaIcon(faStairs);
  public calendar: SVGIcon = this.customFaIcon(faCalendar);
  public cheveronDown: SVGIcon = this.customFaIcon(faChevronDown);
  readonly: boolean = true;
  providerManagementInfo: IProviderManagementInfo;
  currentPopover: PopoverAnchorDirective;
  private sessionTypes: IReferenceData[] = [];

  public user: User;
  public xIcon: SVGIcon = xIcon;
  constructor(public adminService: AdminService, public schedulerEditService:
    SchedulerEditService, private scheduleDialogService: ScheduleDialogService
    , private activatedRoute: ActivatedRoute, private authStore: AuthStore,
    public referenceStore: ReferenceStore,
  ) {
    super();
    this.authStore.UserInfo$.pipe(filter((user: User) => !!user)).subscribe(
      (userInfo: User) => {
        this.user = userInfo;
      }
    );
  }

  ngOnInit(): void {
    this.referenceStore.sessionType$.pipe(takeUntil(this.componentDestroyed)).subscribe((data: IReferenceData[]) => {
      this.sessionTypes = data || [];
    });

    this.schedulerEditService.refershGrid$.pipe(takeUntil(this.componentDestroyed)).subscribe((data: boolean) => {
      if (data) {
        this.schedulerEditService.read(
          this.user?.providerSk,
          startOfWorkWeek,
          endOfWorkWeek
        );
      }
    })
  }

  getGroupSize(data: string): string {
    return data !== undefined ? data : '0';
  }
  getLocationBySessionType(event: number): boolean {
    if (event === this.iEPMeetingAttendanceNumber || event === this.nYCPSAuthorizedTraining ||
      event === this.meetingWithSchoolStaff)
      return true;
    else
      return false;
  }
  getViewScheduleEventData: () => ICurrentEventData | undefined = () => {
    return this.currentEventData;
  };
  onViewScheduleEvent(
    event: SchedulerEvent,
    viewScheduleAnchor: PopoverAnchorDirective
  ): void {
    this.currentEventData = {
      meetingDate: moment(event.dataItem.meetingDate).format('MM/DD/YYYY'),
      startTime: event.start,
      endTime: event.end,
      frequency: event.dataItem.frequency,
      duration: event.dataItem.duration,
      serviceType: event.dataItem.serviceType,
      language: event.dataItem.language,
      meetingSk: event.dataItem.scheduleId,
      floorRoom: event.dataItem.floorRoom,
      teamsMeeting: event.dataItem.teamsMeeting,
      location: event.dataItem.location,
      recurringMeetingSk: event?.dataItem?.recurringMeetingSk,
      assignmentSK: event?.dataItem?.assignmentSK,
      studentName: (event.dataItem.studentName || []),
      sessionType: (event?.dataItem?.sessionType),
      sessionTypeName: (event?.dataItem?.sessionTypeName),
      mandateDescription: (event.dataItem.mandateDescription || []),
      locationType: (event?.dataItem?.locationType),
      selectedStudentMandates: event.dataItem.studentMandates,
      groupSize: event.dataItem.groupSize,
      encounterSk: event.dataItem.encounterSK,
      serviceTypeId:
        event.dataItem.serviceTypeId ??
        event.dataItem.serviceTypeID ??
        event.dataItem.selectedStudentMandates?.[0]?.serviceTypeID ??
        event.dataItem.studentMandates?.[0]?.serviceTypeID,
      studentMandates: (event.dataItem.studentMandates || [])
        .map(
          (m: DashboardPMResponse) => `${m.studentName} (${m.mandateDescription})`
        )
        .join(', '),
    };
    this.currentPopover = viewScheduleAnchor;
    viewScheduleAnchor.show();
  }
  onDateChange(data: DateChangeEvent): void {
    const start: Date = data.dateRange.start;
    const end: Date = data.dateRange.end;

    this.getProviderScheduleDetails(start, end);
  }

  // Fetch provider schedule details from the API
  getProviderScheduleDetails(start: Date, end: Date): void {
    this.schedulerEditService.read(
      this.providerSk,
      formatStartToAPI(start),
      formatEndToAPI(end)
    );
  }
  onCloseViewScheduleEvent(): void {
    if (this.currentPopover) {
      this.currentPopover.hide();
      this.currentPopover = null;
    }
  }
  getSesstionTypeName(sessionTypeId: string): string {
    return this.sessionTypes.find((s) => s.id === sessionTypeId)?.description ?? '';
  }

  // // Assign event colors dynamically
  getEventColor(event: any): string {
    const dataItem = event?.event?.dataItem ?? event?.dataItem ?? event;
    const title = dataItem?.title ?? event?.title;

    if (dataItem?.isHoliday || event?.event?.isHoliday || event?.isHoliday) {
      return 'k-event-holiday';
    }

    switch (title) {
      case 'SHM':
        return 'k-event1';
      case 'IEP':
        return 'k-event2';
      default:
        return 'k-event3';
    }
  }
  customFaIcon(icon: IconDefinition): SVGIcon {
    return {
      name: icon.iconName,
      content: `<path d="${icon.icon[FA_ICON_PATH_INDEX]}" />`,
      viewBox: `0 0 ${icon.icon[FA_ICON_WIDTH_INDEX]} ${icon.icon[FA_ICON_HEIGHT_INDEX]}`,
    };
  }

  displayScheduleTimeDialog(actionButton: string): void {
    const {
      result$,
      instance,
    }: { result$: Observable<DialogResult>; instance: ScheduleTimeComponent } =
      this.scheduleDialogService.open();
    instance.isNew = true;

    if (actionButton === 'schedule time') {
      instance.scheduleTiimeSelected = true;
    } else {
      instance.scheduleTiimeSelected = false;
      instance.displayScheduleTimeButton = false;
    }
    // handle the close result
    result$
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((result: DialogResult | DialogCloseResult | FormGroup) => {
        if (!(result instanceof DialogCloseResult)) {
          if (result instanceof FormGroup) {
            this.submitScheduleEvents(
              result.getRawValue()
            );
          }
        }
      });
  }

  submitScheduleEvents(formValue: ScheduleEventFormValue,
    _mandateInfo?: GetMatchingSchedulesResponse): void {
    void _mandateInfo;
    const assignments: Assignments[] = formValue?.studentMandates?.map(
      (m: DashboardPMResponse) =>
        new Assignments({ assignmentSk: m?.assignmentSk })
    );
    const scheduleEventsData: ScheduleEventSaveModel =
      new ScheduleEventSaveModel({
        ...formValue,
        providerSk: this.providerSk,
        assignments: assignments,
        studentSk: null
      });
    scheduleEventsData.personSk = this.user?.personSk;

    scheduleEventsData.meetingType = ScheduleTimeType;
    scheduleEventsData.subMeetingType = ScheduleTimeSubType;
    scheduleEventsData.isRecurring =
      formValue?.occurences?.id !== Occurrences.DoNotRepeat;
    scheduleEventsData.status = SchedulerStatus.Scheduled;
    scheduleEventsData.sessionType = formValue?.sessionType?.id;
    this.schedulerEditService.scheduleProviderEvents(
      scheduleEventsData,
      formValue?.startDate,
      formValue?.endByDate
    );
  }

  override afterOnDestroy(): void {
    //
  }
}
